/**
 * Brand Color System for VoIPBin Admin Dashboard
 *
 * Centralized color definitions for consistent theming across the application.
 * Uses violet-purple gradient palette as the primary brand identity.
 */

export const BRAND_COLORS = {
  // Primary brand gradient (buttons, active states)
  gradient: {
    primary: 'from-violet-500 to-purple-500',
    primaryHover: 'from-violet-600 to-purple-600',
    primaryDark: 'from-violet-700 to-purple-700',
    primaryDarkHover: 'from-violet-800 to-purple-800',
  },

  // Background gradients
  background: {
    light: 'from-violet-400 via-purple-400 to-violet-500',
    dark: 'from-slate-900 via-slate-900 to-slate-900',
    lightAlt: 'from-violet-400 via-purple-400 to-violet-500',
    darkAlt: 'from-slate-900 via-slate-900/90 to-slate-900',
  },

  // Border colors
  border: {
    light: 'border-violet-200',
    dark: 'dark:border-violet-700/60',
    lightStrong: 'border-violet-300',
    darkStrong: 'dark:border-violet-700/70',
  },

  // Accent colors (cards, highlights)
  accent: {
    light: 'bg-violet-50',
    dark: 'dark:bg-violet-950/60',
    lightHover: 'hover:bg-slate-100',
    darkHover: 'dark:hover:bg-violet-900/70',
  },

  // Text colors
  text: {
    primary: 'text-violet-700',
    primaryDark: 'dark:text-violet-200',
    accent: 'text-violet-600',
    accentDark: 'dark:text-violet-300',
  },

  // Shadow colors
  shadow: {
    light: 'shadow-violet-300/50',
    dark: 'dark:shadow-violet-500/30',
    strong: 'shadow-violet-500/30',
  },

  // Icon/Logo colors
  icon: {
    light: 'text-violet-500',
    lightFilter: 'brightness-0 saturate-100',
    darkFilter: 'dark:brightness-0 dark:invert',
  },
}

/**
 * Helper function to combine brand color classes
 * @param {...string} classes - Brand color class names to combine
 * @returns {string} Combined class string
 */
export const brandColor = (...classes) => classes.join(' ')

/**
 * Common brand color combinations for frequent use cases
 */
export const BRAND_PRESETS = {
  // Active navigation item
  navActive: brandColor(
    'bg-gradient-to-r',
    BRAND_COLORS.gradient.primary,
    'text-white',
    'hover:' + BRAND_COLORS.gradient.primaryHover,
    'shadow-md',
    BRAND_COLORS.shadow.strong
  ),

  // Sidebar background (warm gray in light, dark with subtle blue tint in dark)
  sidebarBg: brandColor(
    'bg-stone-100',
    'dark:bg-gradient-to-b',
    'dark:from-slate-900',
    'dark:via-[#0d1525]',
    'dark:to-slate-900'
  ),

  // Balance/wallet display
  balance: brandColor(
    BRAND_COLORS.accent.light,
    BRAND_COLORS.accent.dark,
    BRAND_COLORS.border.lightStrong,
    BRAND_COLORS.border.darkStrong,
    BRAND_COLORS.accent.lightHover,
    BRAND_COLORS.accent.darkHover
  ),

  // Logo/icon
  logo: brandColor(
    BRAND_COLORS.icon.lightFilter,
    BRAND_COLORS.icon.darkFilter
  ),
}
